import React, { useState, Suspense, useEffect } from 'react';
import 'antd/dist/antd.css';
import { useTranslation } from 'react-i18next';
import trTR from 'antd/es/locale/tr_TR';
import { ConfigProvider, notification, Popconfirm } from 'antd';
import { Tooltip, Row, Button, Table, Col, Form, Tag } from 'antd';
import {
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { ORDER_TYPES, STATUS } from '../../../../helpers/constants';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { useHistory } from 'react-router-dom';
import { PAGE_URLS } from 'helpers/endpoints';
import {
  approveCancelOrder,
  getWaitingCancelItems
} from '../../../../store/requests/order';
import moment from 'moment';

const PartialDeliveryApprovalFilter = React.lazy(
  () => import('./PartialDeliveryApprovalFilter')
);

const initialPageParams = {
  pagination: {
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Toplam ${total} kayıt`,
    pageSizeOptions: [20, 50, 100],
    size: 'small',
    current: 1,
    pageSize: 20
  }
};

const PartialDeliveryApproval = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [filterForm] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOrders, setFilterOrders] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [tableParams, setTableParams] = useState(initialPageParams);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const fetchData = (tableParamsValue, filter) => {
    const queryString = getQueryString(tableParamsValue, filter);
    getWaitingCancelOrder(queryString);
  };

  const getQueryString = (tableParamsValue, filter) => {
    let queryString = '';

    setFilterOrders(filter);
    setSelectedRowKeys([]);
    setSelectedRows([]);

    if (Object.values(filter).some((item) => !!item)) {
      Object.entries(filter)
        .filter(([item, value]) => value !== null && value !== undefined)
        .forEach(([item, value], idx) => {
          if (value) {
            queryString += `${item}=${encodeURI(value)}&`;
          }
        });
    }

    queryString = `${queryString}page=${tableParamsValue.pagination.current - 1}&size=${tableParamsValue.pagination.pageSize}`;

    if (tableParamsValue.column) {
      queryString += `&sort=${tableParamsValue.column.dataIndex},${ORDER_TYPES[tableParamsValue.order]}`;
    }

    return queryString;
  };

  const getWaitingCancelOrder = (queryString) => {
    setLoading(true);
    getWaitingCancelItems(queryString)
      .then((response) => {
        const { content, page } = response.data;
        setRows(content);
        setTableParams({
          pagination: {
            total: page.totalElements,
            current: page.number + 1,
            pageSize: page.size
          }
        });
      })
      .finally(() => setLoading(false));
  };

  const approveCancelOrderHandler = (data, approve) => {
    const requestBody = convertToRequestBody(data, approve);
    approveCancelOrder({ items: requestBody })
      .then((response) => {
        notificationToast(STATUS.SUCCESS, response.data.message);
        fetchData(initialPageParams, filterOrders);
      })
      .catch((err) => {
        console.log('err', err);
      });
  };
  const convertToRequestBody = (data, approve) => {
    const groupedData = {};

    data.forEach((item) => {
      if (!groupedData[item.orderNumber]) {
        groupedData[item.orderNumber] = {
          orderNumber: item.orderNumber,
          products: []
        };
      }

      groupedData[item.orderNumber].products.push({
        productCode: item.productCode,
        cancellationReason: item.cancellationReason,
        approve,
        quantity: item.quantity
      });
    });

    const requestBody = Object.values(groupedData);

    return requestBody;
  };
  const notificationToast = (type, message) => {
    notification[type]({
      description: message
    });
  };

  const columns = [
    {
      title: t('orderNumber'),
      dataIndex: 'customerOrderNumber',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('deliveryDate'),
      dataIndex: 'deliveryDate',
      sorter: true,
      showSorterTooltip: false,
      render: (text) => moment(text).format('DD-MM-YYYY'),
    },
    {
      title: t('Slot'),
      dataIndex: 'timeSlot',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('cancellationDate'),
      dataIndex: 'cancelledDate',
      sorter: true,
      showSorterTooltip: false,
      render: (text) => moment.utc(text).utcOffset(3).format('DD-MM-YYYY HH:mm'),
    },
    {
      title: t('productCode'),
      dataIndex: 'productCode',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('productName'),
      dataIndex: 'productName',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('canceledQuantity'),
      dataIndex: 'quantity',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('reasonForCancellation'),
      dataIndex: 'cancellationReason',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('cancelingPerson'),
      dataIndex: 'cancelledBy',
      sorter: true,
      showSorterTooltip: false
    },
    {
      title: t('storeName'),
      dataIndex: 'storeName',
      sorter: true,
      showSorterTooltip: false
    }
  ];

  const actions = [
    {
      isCrudEnabled,
      type: 'primary',
      title: t('confirmSelected'),
      icon: <CheckOutlined />,
      click: () => approveCancelOrderHandler(selectedRows, true),
      disabled: !selectedRows.length > 0,
      popConfirmTitle: t('confirmSelectedMessage')
    },
    {
      isCrudEnabled,
      type: 'danger',
      title: t('rejectSelected'),
      icon: <CloseOutlined />,
      click: () => approveCancelOrderHandler(selectedRows, false),
      disabled: !selectedRows.length > 0,
      popConfirmTitle: t('rejectSelectedMessage')
    }
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        item.isCrudEnabled && (
          <Tooltip title={item.title} key={idx}>
            <Popconfirm
              title={item?.popConfirmTitle}
              onConfirm={() => item?.click()}
              okText={t('yes')}
              cancelText={t('no')}
              disabled={item?.disabled}
            >
              <Button
                type={item?.type}
                shape="round"
                icon={item?.icon}
                onClick={item.onClick}
                disabled={item.disabled}
                style={{ marginBottom: '10px', marginRight: '3px' }}
                danger={item?.danger}
              />
            </Popconfirm>
          </Tooltip>
        )
      );
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys, selectedRows) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedRows(selectedRows);
    }
  };

  return (
    <div>
      <Suspense fallback={<></>}>
        <PartialDeliveryApprovalFilter
          handlerFilter={(filter) => fetchData(initialPageParams, filter)}
          form={filterForm}
          loading={loading}
        />
      </Suspense>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          marginTop: 20
        }}
      >
        <Col>{renderActionButtons()}</Col>
      </Row>
      <Row style={{ marginTop: 10 }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns.map((column) => ({
              ...column,
              sorter: false
            }))}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            rowSelection={rowSelection}
            onChange={(pagination, filters, sorter) => {
              fetchData({ pagination, ...sorter }, filterOrders);
            }}
            rowKey={(record, idx) =>
              `${record.customerOrderNumber}-${record.productCode}-${idx}`
            }
            scroll={{
              y: '70vh'
            }}
          />
        </ConfigProvider>
      </Row>
    </div>
  );
};

export default PartialDeliveryApproval;
